import { useConfirm } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { removalPlan } from './comic-removal'
import { errorMessage } from './fs-access'
import { useLibraryStore } from '../stores/library'
import { useCloudLibraryStore } from '../stores/cloud-library'

/**
 * 移除單本作品的確認與執行。
 *
 * 整理對話框與卡片動作選單共用這一份：兩個入口講不一樣的話就是兩種行為，而刪除沒有復原。
 * 只有一種處置時是一般的確認，兩種（只移除本機／連雲端一起刪）時用套件的三路決策。
 */
export function useComicRemoval() {
  const { confirm, unsavedChanges } = useConfirm()
  const local = useLibraryStore()
  const cloud = useCloudLibraryStore()

  /** 回傳是否真的移除了。錯誤往上拋給呼叫端顯示。 */
  async function removeComic(comic: Comic): Promise<boolean> {
    const plan = removalPlan(comic)
    if (plan.choices.length === 1) {
      return await confirm({
        title: `從書庫移除「${comic.title}」？`,
        description: plan.description,
        tone: 'danger', confirmLabel: plan.choices[0].label, pendingLabel: '正在移除…',
        errorMessage,
        onConfirm: () => local.deleteComic(comic.id),
      })
    }
    const [preferred, alternative] = plan.choices
    const choice = await unsavedChanges({
      title: `從書庫移除「${comic.title}」？`,
      description: plan.description,
      tone: 'danger',
      saveLabel: preferred.label,
      discardLabel: alternative.label,
      cancelLabel: '取消',
    })
    if (choice === 'cancel') return false
    // 先刪雲端再刪本地：雲端失敗時兩邊都還在，使用者看得到原因也還能重試。
    if (choice === 'discard' && alternative.scope === 'library-and-cloud' && comic.cloudComicId) {
      await cloud.deleteComic(comic.cloudComicId)
    }
    await local.deleteComic(comic.id)
    return true
  }

  return { removeComic }
}
